import type { RadioButtonOption } from '../../ui/RadioButton/RadioButtonGroup.types';
// Type-only import: keeps this module free of any runtime dependency on ApiBase/axios.
import type { Ll144Role } from '../../../services/Ll144AssessmentService';

export const roleOptions: RadioButtonOption[] = [
  { label: 'Employer', value: 'EMPLOYER' },
  { label: 'Employment agency', value: 'EMPLOYMENT_AGENCY' }
];

export const subjectTypeOptions: RadioButtonOption[] = [
  { label: 'Candidates for employment', value: 'CANDIDATES' },
  { label: 'Employees', value: 'EMPLOYEES' },
  { label: 'Both candidates and employees', value: 'CANDIDATES_AND_EMPLOYEES' }
];

export const yesNoOptions: RadioButtonOption[] = [
  { label: 'Yes', value: 'yes' },
  { label: 'No', value: 'no' }
];

export const positionLocationOptions: RadioButtonOption[] = [
  {
    label: 'In a New York City office, at least part of the time',
    value: 'NYC_OFFICE'
  },
  {
    label: 'Fully remote, associated with a New York City office',
    value: 'REMOTE_NYC_OFFICE'
  },
  {
    label: 'Fully remote, not associated with a New York City office',
    value: 'REMOTE_NOT_NYC_ASSOCIATED'
  },
  {
    label: 'In an office outside New York City',
    value: 'NON_NYC_OFFICE'
  }
];

export type Ll144QuestionKey =
  | 'role'
  | 'usesAedt'
  | 'subjectType'
  | 'substantialAssistance'
  | 'positionLocation'
  | 'agencyLocatedInNyc'
  | 'assessedPeopleResideInNyc'
  | 'aedtFirstUseDate';

export type Ll144QuestionGuidance = {
  description: string;
  hint: string;
  definitions: string[];
};

export type Ll144PartId =
  | 'ROLE_SUBJECT'
  | 'AEDT_USE'
  | 'LOCATION'
  | 'FIRST_USE';

export type Ll144Part = {
  id: Ll144PartId;
  number: number;
  title: string;
  questionKeys: Ll144QuestionKey[];
};

export const ll144Parts: Ll144Part[] = [
  {
    id: 'ROLE_SUBJECT',
    number: 1,
    title: 'Role & assessed people',
    questionKeys: ['role', 'subjectType']
  },
  {
    id: 'AEDT_USE',
    number: 2,
    title: 'AEDT use',
    questionKeys: ['usesAedt', 'substantialAssistance']
  },
  {
    id: 'LOCATION',
    number: 3,
    title: 'Where the tool is used',
    questionKeys: [
      'positionLocation',
      'agencyLocatedInNyc',
      'assessedPeopleResideInNyc'
    ]
  },
  {
    // A part of its own rather than a fourth question in LOCATION, because its visibility depends
    // on `assessedPeopleResideInNyc`, which lives there — a conditional question must sit in a
    // LATER part than its trigger or it evaluates against an unanswered value.
    id: 'FIRST_USE',
    number: 4,
    title: 'When the tool is used',
    questionKeys: ['aedtFirstUseDate']
  }
];

export const questionGuidance: Record<Ll144QuestionKey, Ll144QuestionGuidance> = {
  role: {
    description:
      'Identify whether you act as an employer or an employment agency under Local Law 144.',
    hint:
      'Your role changes which NYC Local Law 144 obligations are assigned to this AI system.',
    definitions: [
      'Employer: the organization making or influencing the employment decision.',
      'Employment agency: an agency that recruits, evaluates, or refers candidates or employees for employment decisions.'
    ]
  },
  usesAedt: {
    description:
      'An Automated Employment Decision Tool is a computational process that issues a score, classification, or recommendation and is used to substantially assist or replace discretionary decision-making for employment decisions.',
    hint:
      'If the tool only provides background information without influencing an employment decision, it is usually not treated as an AEDT.',
    definitions: [
      'Automated Employment Decision Tool (AEDT): any computational process, including one derived from machine learning, statistics, or data analytics, that issues a score, classification, or recommendation and is used to substantially assist or replace discretionary decision-making.'
    ]
  },
  subjectType: {
    description:
      'Select whether the tool assesses candidates, employees, or both.',
    hint:
      'The assessed population affects which bias audit and notice obligations apply.',
    definitions: [
      'Candidates for employment: people being considered for a job.',
      'Employees: people already working for the organization.',
      'Both candidates and employees: the tool is used across hiring and existing employment decisions.'
    ]
  },
  substantialAssistance: {
    description:
      'A tool is covered when it substantially assists or replaces discretionary decision-making, not when it merely provides background information.',
    hint:
      'Consider whether a human decision-maker relies on the tool’s score, classification, or recommendation as a material factor.',
    definitions: [
      'Substantial assistance: the tool meaningfully contributes to an employment decision.',
      'Replacement: the tool’s output is used instead of independent human discretion.'
    ]
  },
  positionLocation: {
    description:
      'Local Law 144 applies to an automated employment decision tool that is used in New York City. Where the position sits — not where the candidate or employee lives — is what brings the tool into scope.',
    hint:
      'If the tool is used for more than one position, choose the option that applies to at least one of them.',
    definitions: [
      'In a New York City office: the position requires working from an office in New York City for at least part of the time.',
      'Fully remote, associated with a New York City office: the position has no required office attendance, but the office it reports to is in New York City.',
      'Fully remote, not associated with a New York City office: the position reports to an office outside New York City, or to no office at all.',
      'In an office outside New York City: the position requires working from an office that is not in New York City. An employer based in New York City hiring for such a position is not in scope on location alone.'
    ]
  },
  agencyLocatedInNyc: {
    description:
      'An employment agency that uses an automated employment decision tool from a New York City location is covered by Local Law 144, whatever the location of the positions it recruits for.',
    hint:
      "This is an additional route into scope, not a replacement for the position's location. Either one on its own is enough.",
    definitions: [
      'Located in New York City: the agency uses the tool from a place of business in New York City. One New York City location is enough, even if the agency also operates elsewhere.',
      'Employment agency: an agency that recruits, evaluates, or refers candidates or employees for employment decisions.'
    ]
  },
  assessedPeopleResideInNyc: {
    description:
      'Residency does not affect whether the tool is in scope. It determines who must receive notice before the tool is used.',
    hint:
      'Answering no does not take the tool out of scope. The bias audit, publication and disclosure obligations still apply.',
    definitions: [
      'Resides in New York City: the candidate or employee being assessed lives in New York City, wherever the position is based.',
      'Notice: § 20-871(b)(2) requires each candidate or employee residing in New York City to be told, at least 10 business days before the tool is used, that an automated employment decision tool will be used and which job qualifications and characteristics it will assess.'
    ]
  },
  aedtFirstUseDate: {
    description:
      'Record when this tool is, or will be, first used to screen candidates or employees.',
    hint:
      'The 10-business-day notice window is measured against this date, so a planned date in the future is the right answer for a tool not yet in use.',
    definitions: [
      'First use: the first time the tool is used to assess a candidate or employee, not the date it was bought or configured.',
      'Business days: Monday to Friday, excluding public holidays observed in New York City.',
      'Why it is asked: § 20-871(b)(2) requires notice at least 10 business days before use, which cannot be checked without knowing when use begins.'
    ]
  }
};

export type Ll144AnswerMap = Record<Ll144QuestionKey, string>;

const EMPLOYMENT_AGENCY: Ll144Role = 'EMPLOYMENT_AGENCY';

/**
 * The answers visibility depends on. Named explicitly rather than taking the whole
 * `Ll144AnswerMap` so the callers' useMemo dependency arrays stay honest — passing the map would
 * make every keystroke in any question invalidate them.
 */
export type Ll144VisibilityAnswers = {
  role: string;
  usesAedt: string;
  assessedPeopleResideInNyc: string;
};

/**
 * Reading earlier answers here is safe only because a conditional question's trigger lives in an
 * EARLIER part, which is gated complete before this part is reachable. A conditional question
 * placed in the same part as its trigger would evaluate against an empty string.
 *
 * `aedtFirstUseDate` is asked only when the notice obligation can actually apply: a tool that is
 * not an AEDT is out of scope entirely, and one that screens nobody resident in New York City owes
 * no § 20-871(b)(2) notice, so in both cases the date would be collected and never read.
 */
export const isQuestionVisible = (
  key: Ll144QuestionKey,
  answers: Ll144VisibilityAnswers
): boolean => {
  if (key === 'agencyLocatedInNyc') {
    return answers.role === EMPLOYMENT_AGENCY;
  }
  if (key === 'aedtFirstUseDate') {
    return answers.usesAedt === 'yes' && answers.assessedPeopleResideInNyc === 'yes';
  }
  return true;
};

export const visibleQuestionKeysForPart = (
  part: Ll144Part,
  answers: Ll144VisibilityAnswers
): Ll144QuestionKey[] =>
  part.questionKeys.filter((key) => isQuestionVisible(key, answers));

export const visibleQuestionKeys = (
  answers: Ll144VisibilityAnswers
): Ll144QuestionKey[] =>
  ll144Parts.flatMap((part) => visibleQuestionKeysForPart(part, answers));
